import axiosInstance from 'src/config/axios.config';
import { API_ENDPOINTS } from 'src/config/api.config';

// Interface definitions
export interface SuspiciousLoginAttempt {
  id: string;
  employeeId: string;
  employeeName: string;
  employeeEmail: string;
  branchName?: string | null;
  attemptedDeviceId: string;
  existingDeviceId: string;
  deviceModel: string | null;
  osType: string | null;
  createdAt: string;
  resolutionStatus: SuspiciousLoginResolutionStatus;
  severity: SuspiciousLoginSeverity;
  counterpartyEmployeeId: string | null;
  counterpartyEmployeeName?: string | null;
  reviewedBy: string | null;
  reviewedByName: string | null;
  reviewedAt: string | null;
  resolutionNotes: string | null;
}

export enum SuspiciousLoginResolutionStatus {
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
}

export type SuspiciousLoginSeverity = 'INFO' | 'WARNING' | 'CRITICAL';

export interface SuspiciousLoginResolveRequest {
  resolution: string;
  notes?: string;
}

export interface SuspiciousLoginBatchResolveResult {
  resolved: string[];
  failed: { id: string; reason: string }[];
}

export interface PagedResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
}

// Translation maps for display
export const STATUS_LABELS: Record<SuspiciousLoginResolutionStatus, string> = {
  [SuspiciousLoginResolutionStatus.PENDING]: 'Beklemede',
  [SuspiciousLoginResolutionStatus.APPROVED]: 'Onaylandı',
  [SuspiciousLoginResolutionStatus.REJECTED]: 'Reddedildi',
};

export const STATUS_COLORS: Record<SuspiciousLoginResolutionStatus, 'warning' | 'success' | 'error'> = {
  [SuspiciousLoginResolutionStatus.PENDING]: 'warning',
  [SuspiciousLoginResolutionStatus.APPROVED]: 'success',
  [SuspiciousLoginResolutionStatus.REJECTED]: 'error',
};

export const SEVERITY_LABELS_TR: Record<SuspiciousLoginSeverity, string> = {
  INFO: 'Bilgi',
  WARNING: 'Uyarı',
  CRITICAL: 'Kritik',
};

export const SEVERITY_COLORS: Record<SuspiciousLoginSeverity, 'info' | 'warning' | 'error'> = {
  INFO: 'info',
  WARNING: 'warning',
  CRITICAL: 'error',
};

// Suspicious Login API functions
export const suspiciousLoginApi = {
  // Get all suspicious login attempts with pagination
  async getAllAttempts(
    page: number = 0,
    size: number = 20,
    sortBy: string = 'createdAt',
    sortDirection: string = 'desc',
    status?: SuspiciousLoginResolutionStatus,
    severity?: SuspiciousLoginSeverity,
    branchId?: string
  ): Promise<PagedResponse<SuspiciousLoginAttempt>> {
    const response = await axiosInstance.get<PagedResponse<SuspiciousLoginAttempt>>(
      API_ENDPOINTS.SUSPICIOUS_LOGINS,
      {
        params: {
          page,
          size,
          sortBy,
          sortDirection,
          status,
          severity,
          branchId,
        },
      }
    );
    return response.data;
  },

  // Get pending count for dashboard
  async getPendingCount(): Promise<number> {
    const response = await axiosInstance.get<{ pendingCount: number }>(
      `${API_ENDPOINTS.SUSPICIOUS_LOGINS}/pending-count`
    );
    return response.data.pendingCount;
  },

  // Get a specific attempt by ID
  async getAttemptById(attemptId: string): Promise<SuspiciousLoginAttempt> {
    const response = await axiosInstance.get<SuspiciousLoginAttempt>(
      `${API_ENDPOINTS.SUSPICIOUS_LOGINS}/${attemptId}`
    );
    return response.data;
  },

  // Get attempts for a specific employee
  async getEmployeeAttempts(employeeId: string): Promise<SuspiciousLoginAttempt[]> {
    const response = await axiosInstance.get<SuspiciousLoginAttempt[]>(
      `${API_ENDPOINTS.SUSPICIOUS_LOGINS}/employee/${employeeId}`
    );
    return response.data;
  },

  // Resolve a suspicious login attempt
  async resolveAttempt(
    attemptId: string,
    request: SuspiciousLoginResolveRequest
  ): Promise<SuspiciousLoginAttempt> {
    const response = await axiosInstance.put<SuspiciousLoginAttempt>(
      `${API_ENDPOINTS.SUSPICIOUS_LOGINS}/${attemptId}/resolve`,
      request
    );
    return response.data;
  },

  // Resolve multiple suspicious login attempts in one request
  async resolveBatch(
    attemptIds: string[],
    resolution: string,
    notes?: string
  ): Promise<SuspiciousLoginBatchResolveResult> {
    const response = await axiosInstance.put<SuspiciousLoginBatchResolveResult>(
      `${API_ENDPOINTS.SUSPICIOUS_LOGINS}/resolve-batch`,
      { attemptIds, resolution, notes: notes?.trim() || undefined }
    );
    return response.data;
  },
};
